// src/components/planner/FocusPanel.tsx
"use client";

/**
 * FocusPanel — “focus of the day” bound to day.focus (scoped by iso).
 * - Hydration-safe: local state initializes from day.focus, then syncs via effect
 * - No setState loops: setter only fires when the trimmed focus changes
 * - UX: Save disabled when unchanged; auto-saves on blur
 */

import "./style.css";

import * as React from "react";
import { SectionCard } from "@/components/ui/layout/SectionCard";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/primitives/Input";
import { Button } from "@/components/ui/primitives/Button";
import { useDayFocus } from "./useDayFocus";
import type { ISODate } from "./plannerTypes";

type Props = { iso: ISODate };

export function FocusPanel({ iso }: Props) {
  const { value, setValue, saving, isDirty, lastSavedRef, commit } =
    useDayFocus(iso);
  const headerId = React.useMemo(() => `focus-${iso}-header`, [iso]);
  const focusFieldId = React.useId();
  const focusLabelId = `${focusFieldId}-label`;
  const statusId = `${focusFieldId}-status`;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void commit();
  };

  return (
    <SectionCard className="card-neo-soft">
      <SectionCard.Header
        id={headerId}
        title="Daily Focus"
        titleAs="h6"
        titleClassName="text-title font-semibold tracking-tight"
      />
      <SectionCard.Body>
        <form onSubmit={onSubmit} className="flex items-center gap-[var(--space-2)]">
          <Label htmlFor={focusFieldId} id={focusLabelId} className="sr-only">
            Daily focus
          </Label>
          <Input
            id={focusFieldId}
            placeholder="What’s the one thing today?"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}                   // Auto-save on blur
            name={`focus-${iso}`}
            autoComplete="off"
            className="flex-1"
            aria-labelledby={`${focusLabelId} ${headerId}`}
            aria-describedby={statusId}
          />
          <Button
            type="submit"
            variant="default"
            size="sm"
            disabled={!isDirty || saving}
            aria-disabled={!isDirty || saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </form>

        {/* Subtle status text without yelling at the user */}
        <div
          id={statusId}
          className="mt-[var(--space-2)] text-label text-muted-foreground"
          aria-live="polite"
        >
          {saving
            ? "Saving changes…"
            : isDirty
            ? "Unsaved changes"
            : lastSavedRef.current
            ? "All changes saved"
            : "No focus set yet"}
        </div>
      </SectionCard.Body>
    </SectionCard>
  );
}
